import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGuidedChat,
  useCompleteGuidedChat,
  type MessageSchema,
} from "../hooks/useGuidedChat";
import { ChatContainer } from "../components/ChatContainer";
import { ChatHeader } from "../components/ChatHeader";

export function GuidedChatPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const chatId = id ? parseInt(id, 10) : null;

  const { data: chat, isLoading, error } = useGuidedChat(chatId);
  const completeChat = useCompleteGuidedChat();

  const [messages, setMessages] = useState<MessageSchema[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const handleResize = () => {
      window.scrollTo(0, 0);
      if (containerRef.current) {
        containerRef.current.style.height = `${viewport.height}px`;
      }
    };

    handleResize();
    viewport.addEventListener("resize", handleResize);
    viewport.addEventListener("scroll", handleResize);
    window.addEventListener("scroll", handleResize);
    return () => {
      viewport.removeEventListener("resize", handleResize);
      viewport.removeEventListener("scroll", handleResize);
      window.removeEventListener("scroll", handleResize);
    };
  }, []);

  useEffect(() => {
    if (chat?.messages) {
      setMessages(chat.messages);
    }
  }, [chat?.messages]);

  const handleBack = () => {
    navigate("/");
  };

  const handleClose = () => {
    navigate("/");
  };

  const handleFinish = () => {
    if (chatId) {
      completeChat.mutate(chatId, {
        onSuccess: () => {
          navigate(`/guided-chat/${chatId}/complete`);
        },
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center bg-[#F8F8F6]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-primary border-t-transparent" />
      </div>
    );
  }

  if (error || !chat) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center bg-[#F8F8F6]">
        <div className="text-text-error-primary">Chat not found</div>
      </div>
    );
  }

  const isCompleted = chat.status === "completed";

  // Get the initial question (first assistant message)
  const initialQuestion =
    messages.length > 0 && messages[0].role === "assistant"
      ? messages[0].content
      : undefined;

  // Show finish button after user has sent at least one message
  const hasUserMessage = messages.some((m) => m.role === "user");
  const showFinish = hasUserMessage && !isCompleted;

  return (
    <div ref={containerRef} className="flex flex-col bg-[#F8F8F6]" style={{ height: "100dvh" }}>
      {/* Constrained container like onboarding */}
      <div className="flex-1 min-h-0 flex flex-col max-w-md mx-auto w-full h-full">
        <ChatHeader
          onBack={handleBack}
          onClose={handleClose}
          showFinish={showFinish}
          onFinish={handleFinish}
          isFinishing={completeChat.isPending}
        />

        <div className="flex-1 min-h-0 overflow-hidden">
          <ChatContainer
            chatId={chatId!}
            messages={messages}
            onMessagesUpdate={setMessages}
            isCompleted={isCompleted}
            initialQuestion={initialQuestion}
            showFinish={showFinish}
            onFinish={handleFinish}
            isFinishing={completeChat.isPending}
          />
        </div>
      </div>
    </div>
  );
}
